import { isPlatformBrowser } from '@angular/common';
import { EnvironmentProviders, inject, NgModule, PLATFORM_ID, provideAppInitializer } from '@angular/core';

import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import {
  AppCheck,
  initializeAppCheck,
  provideAppCheck,
  ReCaptchaEnterpriseProvider,
} from '@angular/fire/app-check';
import { getAuth, provideAuth } from '@angular/fire/auth';
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  provideFirestore
} from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { TimeagoFormatter, TimeagoModule } from 'ngx-timeago';

import { GoogleSignInService } from '../shared/services/google-sign-in.service';
import { TimeagoShortFormatter } from '../shared/timeago-short.formatter';
import { environment } from 'src/environments/environment';

@NgModule({
  imports: [
    TimeagoModule.forRoot({
      formatter: { provide: TimeagoFormatter, useClass: TimeagoShortFormatter },
    }),
  ],
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    ...createAppCheckProviders(),
    provideAuth(() => getAuth()),
    provideFirestore(() => createFirestoreInstance(inject(PLATFORM_ID))),
    provideFunctions(() => getFunctions(getApp(), 'australia-southeast1')),
    provideStorage(() => getStorage()),
    provideAnalytics(() => getAnalytics()),
    providePerformance(() => getPerformance()),
    provideAppInitializer(() => {
      inject(AppCheck, { optional: true });
    }),
    // Must finish before the router runs, so the sign in page's guard never sees a Google session
    // that has not had the SSO rules applied to it yet. No-op under the popup flow.
    provideAppInitializer(() => inject(GoogleSignInService).complete()),
  ]
})
export class CoreServicesModule { }

function createAppCheckProviders(): EnvironmentProviders[] {
  const siteKey = normalizeString(environment.recaptchaEnterpriseSiteKey);

  if (!siteKey || !canUseBrowserAppCheck()) {
    return [];
  }

  return [
    provideAppCheck(() => initializeAppCheck(getApp(), {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    })),
  ];
}

function canUseBrowserAppCheck(): boolean {
  return typeof document !== 'undefined' && typeof document.createElement === 'function';
}

function createFirestoreInstance(platformId: object) {
  const app = getApp();

  if (!isPlatformBrowser(platformId)) {
    return getFirestore(app);
  }

  try {
    if (shouldUseMemoryCache()) {
      return initializeFirestore(app, {
        localCache: memoryLocalCache(),
      });
    }

    return initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch {
    return getFirestore(app);
  }
}

function shouldUseMemoryCache(): boolean {
  if (!environment.production) {
    return true;
  }

  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length ? normalizedValue : null;
}
